/**
 * LetterDialog — generates HR letter PDFs for an employee:
 *   1. Offer Intimation       (pre-joining short letter)
 *   2. Appointment Letter      (full letter with Annexure salary breakup)
 *   3. Salary Revision         (raise / revision notification)
 *   4. Experience Certificate  (only when employee marked LEFT)
 *   5. Relieving Letter        (only when employee marked LEFT)
 *   6. No-Dues Certificate     (declaration on company letterhead)
 *
 * Pre-fills from the employee record + active company, lets the admin tweak
 * fields, then POSTs to /api/employees/{id}/letters/* and downloads the PDF.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  FileText, EnvelopeSimple, Download, Buildings, Sparkle,
  Medal, SignOut, ShieldCheck, TrendUp, LockKey, FileDoc,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthYearLabel = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  } catch { return ''; }
};
const autoRef = (companyShort, dateISO) => {
  const d = dateISO ? new Date(dateISO) : new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `DC/HR/${companyShort || 'Chn'}/${mm}/${d.getFullYear()}`;
};
const monthlyOf = (sc = {}) =>
  Number(sc.basic || 0) + Number(sc.hra || 0) + Number(sc.con || 0)
  + Number(sc.medical || 0) + Number(sc.special_allowance || 0) + Number(sc.non_taxable || 0);

const Field = ({ label, hint, children, full = false }) => (
  <div className={full ? 'col-span-2 space-y-1' : 'space-y-1'}>
    <Label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">{label}</Label>
    {children}
    {hint && <p className="text-[10px] text-gray-400 mt-0.5">{hint}</p>}
  </div>
);

// Tab catalog — single source of truth for icon, label, sub-text and endpoint.
const TAB_DEFS = [
  { key: 'offer',       label: 'Offer Intimation',  sub: 'Short pre-joining letter', icon: EnvelopeSimple, endpoint: 'offer-intimation',  needsLeft: false },
  { key: 'appointment', label: 'Appointment',       sub: 'Full letter + salary annexure', icon: FileText,    endpoint: 'appointment',       needsLeft: false },
  { key: 'salary_rev',  label: 'Salary Revision',   sub: 'Upward revision notification',  icon: TrendUp,     endpoint: 'salary-revision',   needsLeft: false },
  { key: 'experience',  label: 'Experience',        sub: 'Certificate of employment',     icon: Medal,       endpoint: 'experience',        needsLeft: true  },
  { key: 'relieving',   label: 'Relieving',         sub: 'Acceptance of resignation',     icon: SignOut,     endpoint: 'relieving',         needsLeft: true  },
  { key: 'no_dues',     label: 'No-Dues',           sub: 'Exit declaration certificate',  icon: ShieldCheck, endpoint: 'no-dues',           needsLeft: false },
];

export default function LetterDialog({ employee, open, onClose }) {
  const [tab, setTab] = useState('offer');
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [generating, setGenerating] = useState(false);

  // Shared fields
  const [refNo, setRefNo] = useState('');
  const [letterDate, setLetterDate] = useState(todayISO());
  const [position, setPosition] = useState('');
  const [joiningDate, setJoiningDate] = useState('');
  const [permanentAddress, setPermanentAddress] = useState('');
  const [guardianName, setGuardianName] = useState('');
  const [signatoryName, setSignatoryName] = useState('');
  const [signatoryTitle, setSignatoryTitle] = useState('Partner');

  // Offer-intimation
  const [monthlyConsolidated, setMonthlyConsolidated] = useState(0);

  // Appointment letter
  const [noticeResign, setNoticeResign] = useState('45 Days');
  const [noticeTerm, setNoticeTerm] = useState('2 Months');
  const [probation, setProbation] = useState('Six months');
  const [minDuration, setMinDuration] = useState('One Full Year');
  const [location, setLocation] = useState('Chennai');
  const [salaryComponents, setSalaryComponents] = useState({
    basic: 0, hra: 0, con: 0, medical: 0, special_allowance: 0, non_taxable: 0,
  });

  // Experience / Relieving / No-Dues
  const [relievingDate, setRelievingDate] = useState('');
  const [subjectLine, setSubjectLine] = useState('Your Resignation');
  const [city, setCity] = useState('Chennai');
  const [responsibilities, setResponsibilities] = useState('');
  const [validTillYear, setValidTillYear] = useState(new Date().getFullYear());

  // Salary revision
  const [newMonthly, setNewMonthly] = useState(0);
  const [effectiveMonth, setEffectiveMonth] = useState(monthYearLabel(todayISO()));

  // Load companies on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axios.get(`${API_URL}/api/companies`, { withCredentials: true })
      .then((r) => {
        if (cancelled) return;
        const list = r.data || [];
        setCompanies(list);
        const def = list.find((c) => c.is_default) || list[0];
        if (def) {
          setCompanyId(def.id);
          setSignatoryName(def.signatory || 'S. Babu');
          setSignatoryTitle(def.signature_label || 'Partner');
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  // Reset employee-derived state whenever the dialog opens for a new employee
  useEffect(() => {
    if (!open || !employee) return;
    const sc = employee.salary_components || {};
    setSalaryComponents({
      basic: Number(sc.basic || 0),
      hra: Number(sc.hra || 0),
      con: Number(sc.con || 0),
      medical: Number(sc.medical || 0),
      special_allowance: Number(sc.special_allowance || 0),
      non_taxable: Number(sc.non_taxable || 0),
    });
    const total = monthlyOf(sc);
    setMonthlyConsolidated(total);
    setNewMonthly(total);
    setPosition(employee.designation_name || '');
    setJoiningDate(employee.joining_date || todayISO());
    setPermanentAddress(employee.permanent_address || employee.current_address || '');
    setGuardianName('');
    setLetterDate(todayISO());
    setRelievingDate(employee.left_date || '');
    setSubjectLine('Your Resignation');
    setCity('Chennai');
    setResponsibilities('');
    setValidTillYear(new Date().getFullYear());
    setEffectiveMonth(monthYearLabel(todayISO()));
    // If the employee is still active, default the tab to one that doesn't need LEFT.
    setTab((prev) => {
      const def = TAB_DEFS.find((t) => t.key === prev);
      const isLeft = employee.is_active === false && !!employee.left_date;
      if (def?.needsLeft && !isLeft) return 'offer';
      return prev;
    });
  }, [open, employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh ref number on company/date change
  const company = useMemo(() => companies.find((c) => c.id === companyId) || null, [companies, companyId]);
  useEffect(() => {
    if (!company) return;
    setRefNo(autoRef(company.short, letterDate));
  }, [company?.id, letterDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthlyTotal = useMemo(() => monthlyOf(salaryComponents), [salaryComponents]);
  const annualTotal = monthlyTotal * 12;
  const employeeIsLeft = !!(employee && employee.is_active === false && employee.left_date);
  const activeTabDef = TAB_DEFS.find((t) => t.key === tab) || TAB_DEFS[0];
  const blockedReason = activeTabDef.needsLeft && !employeeIsLeft
    ? 'Mark the employee as LEFT and set their Last Working Day to enable this letter.'
    : '';

  const generate = async (fmt = 'pdf') => {
    if (!employee?.id) return;
    if (blockedReason) { toast.error(blockedReason); return; }
    setGenerating(fmt);
    try {
      const url = `${API_URL}/api/employees/${employee.id}/letters/${activeTabDef.endpoint}?format=${fmt}`;
      const common = {
        ref_no: refNo,
        letter_date: letterDate,
        position,
        joining_date: joiningDate,
        permanent_address: permanentAddress,
        guardian_name: guardianName,
        signatory_name: signatoryName,
        signatory_title: signatoryTitle,
        company_id: companyId,
      };
      let body = common;
      if (tab === 'offer') {
        body = { ...common, monthly_consolidated: Number(monthlyConsolidated || 0) };
      } else if (tab === 'appointment') {
        body = {
          ...common,
          notice_period_resignation: noticeResign,
          notice_period_termination: noticeTerm,
          probation_period: probation,
          min_duration: minDuration,
          location,
          salary_components: salaryComponents,
        };
      } else if (tab === 'experience') {
        body = {
          ...common,
          relieving_date: relievingDate,
          monthly_consolidated: Number(monthlyConsolidated || 0),
        };
      } else if (tab === 'relieving') {
        body = { ...common, relieving_date: relievingDate, subject: subjectLine };
      } else if (tab === 'no_dues') {
        body = {
          ...common,
          city,
          responsibilities,
          valid_till_year: Number(validTillYear || new Date().getFullYear()),
        };
      } else if (tab === 'salary_rev') {
        body = {
          ...common,
          new_monthly_consolidated: Number(newMonthly || 0),
          effective_month: effectiveMonth,
        };
      }
      const r = await axios.post(url, body, { withCredentials: true, responseType: 'blob' });
      const mimeType = fmt === 'docx'
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : 'application/pdf';
      const blob = new Blob([r.data], { type: mimeType });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const fnMap = {
        offer: 'OfferIntimation_',
        appointment: 'AppointmentLetter_',
        salary_rev: 'SalaryRevisionLetter_',
        experience: 'ExperienceCertificate_',
        relieving: 'RelievingLetter_',
        no_dues: 'NoDuesCertificate_',
      };
      a.download = (fnMap[tab] || 'Letter_') + (employee.name || 'employee').replace(/\s+/g, '_') + '.' + fmt;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
      toast.success(`${activeTabDef.label} ${fmt === 'docx' ? 'Word document' : 'PDF'} generated`);
    } catch (e) {
      const msg = e.response?.data?.detail
        || (e.response?.data instanceof Blob ? await e.response.data.text() : null)
        || e.message;
      toast.error('Generation failed: ' + msg);
    } finally {
      setGenerating(false);
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="letter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1D9E75 0%, #047857 100%)' }}>
              <FileText size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="text-base font-bold">Generate Letter</div>
              <div className="text-[11px] font-normal text-gray-500">for <span className="font-semibold text-gray-700">{employee.name}</span> · {employee.emp_id}{employeeIsLeft && <span className="ml-2 inline-block text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">● LEFT · LWD {employee.left_date}</span>}</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Tab grid — 6 letter types as cards */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          {TAB_DEFS.map((t) => {
            const Icon = t.icon;
            const disabled = t.needsLeft && !employeeIsLeft;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { if (!disabled) setTab(t.key); }}
                disabled={disabled}
                title={disabled ? 'Available only when employee is marked LEFT' : ''}
                className={`text-left px-3 py-2.5 rounded-lg border-2 transition-all ${active ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`tab-${t.key}`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={15} weight="fill" className={active ? 'text-emerald-600' : 'text-gray-400'} />
                  <div className={`font-bold text-[13px] ${active ? 'text-emerald-700' : 'text-gray-700'}`}>{t.label}</div>
                  {disabled && <LockKey size={11} className="text-gray-400" />}
                </div>
                <div className="text-[10px] text-gray-500 leading-snug">{t.sub}</div>
              </button>
            );
          })}
        </div>

        {blockedReason && (
          <div className="px-3 py-2 mb-3 text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-800" data-testid="letter-blocked-banner">
            ⚠️ {blockedReason}
          </div>
        )}

        {/* Company picker */}
        <div className="border rounded-lg p-3 bg-slate-50 mb-3">
          <div className="flex items-center gap-2 mb-2">
            <Buildings size={14} className="text-slate-500" />
            <Label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Letterhead Company</Label>
          </div>
          <div className="flex flex-wrap gap-2">
            {companies.length === 0 ? (
              <span className="text-xs text-gray-500 italic">No companies configured. Add one in Settings → Companies.</span>
            ) : companies.map((c) => (
              <button
                key={c.id}
                onClick={() => {
                  setCompanyId(c.id);
                  if (c.signatory) setSignatoryName(c.signatory);
                  if (c.signature_label) setSignatoryTitle(c.signature_label);
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-md border-2 text-xs font-semibold transition-all ${companyId === c.id ? 'border-emerald-500 bg-white text-emerald-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}
                data-testid={`company-${c.id}`}
              >
                {c.logo_url && <img src={c.logo_url.startsWith('http') ? c.logo_url : `${API_URL}${c.logo_url}`} alt="" className="h-5 max-w-[60px] object-contain" />}
                <span>{c.name}</span>
                {c.is_default && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">DEFAULT</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Common fields — shown for every tab */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          {/* Ref no. only useful on the offer/appointment letters */}
          {(tab === 'offer' || tab === 'appointment') && (
            <Field label="Letter Reference No." hint="Auto-generated — edit if needed">
              <Input value={refNo} onChange={(e) => setRefNo(e.target.value)} data-testid="ref-no" />
            </Field>
          )}
          <Field label="Letter Date">
            <Input type="date" value={letterDate} onChange={(e) => setLetterDate(e.target.value)} data-testid="letter-date" />
          </Field>
          {(tab === 'offer' || tab === 'appointment' || tab === 'experience' || tab === 'no_dues') && (
            <Field label="Position / Designation">
              <Input value={position} onChange={(e) => setPosition(e.target.value)} placeholder="e.g. Business Analyst" data-testid="position" />
            </Field>
          )}
          {(tab === 'offer' || tab === 'appointment' || tab === 'experience') && (
            <Field label="Date of Joining">
              <Input type="date" value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} data-testid="joining-date" />
            </Field>
          )}
          {(tab === 'offer' || tab === 'appointment' || tab === 'relieving' || tab === 'no_dues' || tab === 'salary_rev') && (
            <Field label="Permanent Address" full>
              <textarea value={permanentAddress} onChange={(e) => setPermanentAddress(e.target.value)}
                rows={2} className="w-full border rounded px-3 py-2 text-sm resize-y"
                placeholder="Door no, street, city, state — pin code" data-testid="permanent-address" />
            </Field>
          )}
          {(tab === 'offer' || tab === 'relieving' || tab === 'no_dues' || tab === 'salary_rev') && (
            <Field label="Father's / Guardian's Name" hint="e.g. Mr. Rajan — optional">
              <Input value={guardianName} onChange={(e) => setGuardianName(e.target.value)} placeholder="optional" data-testid="guardian-name" />
            </Field>
          )}
        </div>

        {/* Offer Intimation specific */}
        {tab === 'offer' && (
          <div className="border rounded-lg p-3 mb-3" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)' }}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1">
              <Sparkle size={12} weight="fill" /> Offer Intimation details
            </div>
            <Field label="Monthly Consolidated Cost (₹)" hint="Auto-filled from salary components">
              <Input type="number" value={monthlyConsolidated} onChange={(e) => setMonthlyConsolidated(e.target.value)} data-testid="monthly-cost" />
            </Field>
          </div>
        )}

        {/* Appointment letter specific */}
        {tab === 'appointment' && (
          <>
            <div className="border rounded-lg p-3 mb-3" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)' }}>
              <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2">Clauses</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Notice — Resignation" hint="During / after probation">
                  <Input value={noticeResign} onChange={(e) => setNoticeResign(e.target.value)} data-testid="notice-resign" />
                </Field>
                <Field label="Notice — Termination" hint="From company side after confirmation">
                  <Input value={noticeTerm} onChange={(e) => setNoticeTerm(e.target.value)} data-testid="notice-term" />
                </Field>
                <Field label="Probation Period">
                  <Input value={probation} onChange={(e) => setProbation(e.target.value)} data-testid="probation" />
                </Field>
                <Field label="Minimum Employment Duration">
                  <Input value={minDuration} onChange={(e) => setMinDuration(e.target.value)} data-testid="min-duration" />
                </Field>
                <Field label="Work Location">
                  <Input value={location} onChange={(e) => setLocation(e.target.value)} data-testid="location" />
                </Field>
              </div>
            </div>

            <div className="border rounded-lg p-3 mb-3" style={{ background: 'linear-gradient(135deg, #fefce8 0%, #ffffff 100%)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Annexure A — Salary Breakup</div>
                <div className="text-xs text-gray-500">
                  Monthly: <span className="font-bold text-amber-700">₹{monthlyTotal.toLocaleString('en-IN')}</span> ·
                  Annual: <span className="font-bold text-emerald-700">₹{annualTotal.toLocaleString('en-IN')}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['basic', 'hra', 'con', 'medical', 'special_allowance', 'non_taxable'].map((k) => (
                  <div key={k} className="space-y-1">
                    <Label className="text-[10px] uppercase font-bold text-gray-500">
                      {k === 'con' ? 'Conveyance' : k === 'special_allowance' ? 'Special' : k === 'non_taxable' ? 'Other' : k}
                    </Label>
                    <Input
                      type="number"
                      value={salaryComponents[k] || 0}
                      onChange={(e) => setSalaryComponents({ ...salaryComponents, [k]: Number(e.target.value) || 0 })}
                      className="text-sm"
                      data-testid={`sal-${k}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Experience Certificate specific */}
        {tab === 'experience' && (
          <div className="border rounded-lg p-3 mb-3" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)' }}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1">
              <Medal size={12} weight="fill" /> Experience Certificate
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of Relieving" hint="Defaults to the employee's Last Working Day">
                <Input type="date" value={relievingDate} onChange={(e) => setRelievingDate(e.target.value)} data-testid="relieving-date" />
              </Field>
              <Field label="Salary per Month (₹)" hint="Auto-filled — edit if needed">
                <Input type="number" value={monthlyConsolidated} onChange={(e) => setMonthlyConsolidated(e.target.value)} data-testid="monthly-cost" />
              </Field>
            </div>
          </div>
        )}

        {/* Relieving Letter specific */}
        {tab === 'relieving' && (
          <div className="border rounded-lg p-3 mb-3" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)' }}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1">
              <SignOut size={12} weight="fill" /> Relieving details
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date of Relieving" hint="Defaults to Last Working Day">
                <Input type="date" value={relievingDate} onChange={(e) => setRelievingDate(e.target.value)} data-testid="relieving-date" />
              </Field>
              <Field label="Subject Line" hint="Defaults to 'Your Resignation'">
                <Input value={subjectLine} onChange={(e) => setSubjectLine(e.target.value)} data-testid="subject-line" />
              </Field>
            </div>
          </div>
        )}

        {/* No-Dues Certificate specific */}
        {tab === 'no_dues' && (
          <div className="border rounded-lg p-3 mb-3" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)' }}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1">
              <ShieldCheck size={12} weight="fill" /> No-Dues details
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City">
                <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="city" />
              </Field>
              <Field label="Valid Till Year" hint="The declaration remains valid till">
                <Input type="number" value={validTillYear} onChange={(e) => setValidTillYear(e.target.value)} data-testid="valid-till-year" />
              </Field>
              <Field label="Roles & Responsibilities Handled" full hint="e.g. Project SPOC, Project Coordinator, Presales, Project Leader">
                <textarea
                  value={responsibilities}
                  onChange={(e) => setResponsibilities(e.target.value)}
                  rows={2}
                  className="w-full border rounded px-3 py-2 text-sm resize-y"
                  placeholder="Comma-separated activities the employee was responsible for"
                  data-testid="responsibilities"
                />
              </Field>
            </div>
          </div>
        )}

        {/* Salary Revision specific */}
        {tab === 'salary_rev' && (
          <div className="border rounded-lg p-3 mb-3" style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)' }}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1">
              <TrendUp size={12} weight="fill" /> Salary Revision details
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Revised Monthly Salary (₹)" hint="The new consolidated monthly amount">
                <Input type="number" value={newMonthly} onChange={(e) => setNewMonthly(e.target.value)} data-testid="new-monthly" />
              </Field>
              <Field label="Effective Month" hint="e.g. Oct 2022">
                <Input value={effectiveMonth} onChange={(e) => setEffectiveMonth(e.target.value)} data-testid="effective-month" />
              </Field>
            </div>
          </div>
        )}

        {/* Signatory */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Field label="Signatory Name">
            <Input value={signatoryName} onChange={(e) => setSignatoryName(e.target.value)} data-testid="signatory-name" />
          </Field>
          <Field label="Signatory Title">
            <Input value={signatoryTitle} onChange={(e) => setSignatoryTitle(e.target.value)} data-testid="signatory-title" />
          </Field>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="outline"
            onClick={() => generate('docx')}
            disabled={!!generating || !!blockedReason}
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
            data-testid="generate-letter-docx-btn"
          >
            <FileDoc size={14} className="mr-1.5" weight="bold" />
            {generating === 'docx' ? 'Generating Word…' : 'Download Word'}
          </Button>
          <Button
            onClick={() => generate('pdf')}
            disabled={!!generating || !!blockedReason}
            className="text-white"
            style={{ background: 'linear-gradient(135deg, #1D9E75 0%, #047857 100%)' }}
            data-testid="generate-letter-btn"
          >
            <Download size={14} className="mr-1.5" weight="bold" />
            {generating === 'pdf' ? 'Generating PDF…' : 'Download PDF'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
